import type { Family, GameConfig } from "../domain/types.js";
import type { Attempt, Problem, Profile, Random } from "./types.js";
import { START_RATINGS, TEMPLATES } from "./templates.js";
import { makeDistractors, shuffled } from "./distractors.js";
export function choiceCount(rating: number, family: Family): number | null {
    const progress = Math.max(0, rating - START_RATINGS[family]);
    return progress < 100 ? 2 : progress < 200 ? 3 : progress < 300 ? 4 : progress < 400 ? 6 : null;
}
export function expectedSuccess(playerRating: number, problemRating: number, choices: number | null): number {
    const g = choices ? 1 / choices : 0;
    return g + (1 - g) / (1 + 10 ** ((problemRating - playerRating) / 400));
}
export function updateRating(before: number, difficulty: number, correct: boolean, count: number | null, attempts: number): number {
    const k = 16 + 24 * Math.exp(-attempts / 12);
    return Math.max(400, Math.min(2200, before + k * (Number(correct) - expectedSuccess(before, difficulty, count))));
}
export function unlockSkills(p: Profile): Family[] {
    const unlocked: Family[] = [];
    const can: Record<Family, boolean> = { add: true, sub: p.skills.add.rating >= 900, missing: p.skills.add.rating >= 1000, mul: p.skills.add.rating >= 1050, div: p.skills.mul.rating >= 1200 && p.unlocked.includes("mul") };
    for (const family of ["add", "sub", "missing", "mul", "div"] as Family[])
        if (can[family] && !p.unlocked.includes(family)) {
            p.unlocked.push(family);
            unlocked.push(family);
        }
    return unlocked;
}
export function selectProblem(p: Profile, c: GameConfig, random: Random, forcedFamily?: Family): Problem {
    unlockSkills(p);
    const family = forcedFamily && p.unlocked.includes(forcedFamily) ? forcedFamily : [...p.unlocked].sort((a, b) => (p.skills[a].rating - START_RATINGS[a]) - (p.skills[b].rating - START_RATINGS[b]) || p.skills[a].attempts - p.skills[b].attempts || (a < b ? -1 : 1))[0];
    const rating = p.skills[family].rating, count = choiceCount(rating, family);
    const choices = TEMPLATES.filter(t => t.family === family).map(t => ({ t, distance: Math.abs(expectedSuccess(rating, t.rating, count) - 0.8) }));
    choices.sort((a, b) => a.distance - b.distance || a.t.rating - b.t.rating);
    const template = choices[0].t, n = template.generate(random);
    const difficulty = template.rating + Math.min(40, Math.max(-40, Math.round((n.a + n.b - 15) / 4)));
    const distractors = count ? makeDistractors(family, n.a, n.b, n.answer, count - 1, random) : [];
    const options = count ? shuffled([String(n.answer), ...distractors.map(d => String(d.value))], random) : null;
    const wrapper = (count === null || count >= 6) ? c.wordProblems.filter(w => w.family === family) : [];
    const wordText = wrapper.length && random.float() < 0.35 ? wrapper[random.int(0, wrapper.length - 1)].text.replace("{a}", String(n.a)).replace("{b}", String(n.b)) : undefined;
    return { id: `${template.id}-${random.int(0, 2147483647)}`, templateId: template.id, family, rating: difficulty, ...n, choices: options, distractors, wordText };
}
export function recordAttempt(p: Profile, problem: Problem, answer: string, responseMs: number, at: number): {
    attempt: Attempt;
    unlocked: Family[];
    moreKeys: boolean;
} {
    const skill = p.skills[problem.family], before = skill.rating, oldChoiceCount = choiceCount(before, problem.family);
    const correct = /^(0|[1-9][0-9]{0,4})$/.test(answer) && Number(answer) === problem.answer;
    const pattern = correct ? null : problem.distractors.find(d => String(d.value) === answer)?.pattern || "other";
    skill.rating = updateRating(before, problem.rating, correct, problem.choices?.length || null, skill.attempts);
    skill.attempts++;
    if (correct)
        skill.correct++;
    p.totalAttempts++;
    const attempt: Attempt = { at, template: problem.templateId, family: problem.family, a: problem.a, b: problem.b, correctAnswer: problem.answer, choices: problem.choices ? [...problem.choices] : null, answer, pattern, correct, responseMs: Math.max(0, Math.min(86400000, Math.round(responseMs))), ratingBefore: before, ratingAfter: skill.rating };
    p.attempts.push(attempt);
    if (p.attempts.length > 1000)
        p.attempts.shift();
    const key = problem.expression, fact = p.facts[key] || { attempts: 0, missed: 0 };
    fact.attempts++;
    if (!correct)
        fact.missed++;
    p.facts[key] = fact;
    if (Object.keys(p.facts).length > 2000)
        delete p.facts[Object.keys(p.facts)[0]];
    if (pattern)
        p.patterns[pattern] = (p.patterns[pattern] || 0) + 1;
    return { attempt, unlocked: unlockSkills(p), moreKeys: choiceCount(skill.rating, problem.family) !== oldChoiceCount && skill.rating > before };
}
